import React from 'react';
import {
  Box,
  IconButton,
  Typography,
  Autocomplete,
  TextField,
  Paper,
} from '@mui/material';
import { useCategories } from 'application/hooks/useCategories';
import { useImportWizardStore } from '../../store/useImportWizardStore';
import ColumnSelect from './ColumnSelect';

interface ContactFieldConfig {
  show: boolean;
  required?: boolean;
}

interface FlexField {
  name: string;
  label?: string;
  type?: string;
  contactFields?: Record<string, ContactFieldConfig>;
  [key: string]: any;
}

/**
 * Flex attribute mapping using the category's configured flex fields.
 *
 * Special handling:
 * - type "ImageList": excluded (not mappable from CSV)
 * - type "Contact": expands into sub-fields from contactFields config,
 *   showing only fields with show:true, marking required ones
 * - All other types: standard flex field → column mapping
 */
const FlexAttributeMapping: React.FC = () => {
  const headers = useImportWizardStore((s) => s.headers);
  const categoryID = useImportWizardStore(
    (s) => s.mappingConfig.categoryID ?? ''
  );
  const columnMappings = useImportWizardStore(
    (s) => s.mappingConfig.columnMappings
  );
  const updateMappingConfig = useImportWizardStore(
    (s) => s.updateMappingConfig
  );

  const { categories } = useCategories();
  const selectedCategory = categories.find((cat) => cat.id === categoryID);

  // Get flex fields from category config
  const flexFieldsConfig: FlexField[] =
    selectedCategory?.flexAttributes?.config?.deal?.flexFields ?? [];

  // Separate fields by type
  const contactFields = flexFieldsConfig.filter((f) => f.type === 'Contact');
  const regularFields = flexFieldsConfig.filter(
    (f) => f.type !== 'ImageList' && f.type !== 'Contact'
  );

  const flexAttributes: Record<string, string> =
    columnMappings?.flexAttributes ?? {};

  const updateFlexAttributes = (newFlexAttributes: Record<string, string>) => {
    updateMappingConfig({
      columnMappings: {
        dealFields: columnMappings?.dealFields ?? { extID: '' },
        ...columnMappings,
        flexAttributes: newFlexAttributes,
      },
    });
  };

  const handleColumnChange = (key: string, value: string) => {
    updateFlexAttributes({
      ...flexAttributes,
      [key]: value,
    });
  };

  const handleAddRow = (field: FlexField) => {
    if (field.type === 'Contact' && field.contactFields) {
      // Add all visible sub-fields at once
      const newEntries: Record<string, string> = {};
      Object.entries(field.contactFields).forEach(([subKey, config]) => {
        if (config.show) {
          newEntries[`${field.name}.${subKey}`] = '';
        }
      });
      updateFlexAttributes({ ...flexAttributes, ...newEntries });
    } else {
      updateFlexAttributes({ ...flexAttributes, [field.name]: '' });
    }
  };

  const handleRemoveRow = (key: string) => {
    const updated = { ...flexAttributes };
    delete updated[key];
    updateFlexAttributes(updated);
  };

  const getFieldLabel = (name: string): string => {
    const field = flexFieldsConfig.find((f) => f.name === name);
    return field?.label || field?.name || name;
  };

  // Regular fields that haven't been mapped yet
  const usedFieldNames = new Set(Object.keys(flexAttributes));
  const availableRegularFields = regularFields.filter(
    (f) => !usedFieldNames.has(f.name)
  );

  // Contact fields that haven't been added yet
  const addedContactFieldNames = new Set(
    contactFields
      .filter((cf) => {
        const visibleSubFields = Object.entries(cf.contactFields || {}).filter(
          ([, config]) => config.show
        );
        return visibleSubFields.some(
          ([subKey]) => `${cf.name}.${subKey}` in flexAttributes
        );
      })
      .map((cf) => cf.name)
  );
  const availableContactFields = contactFields.filter(
    (cf) => !addedContactFieldNames.has(cf.name)
  );

  // Combined available fields for the "Add" picker
  const availableFields: FlexField[] = [
    ...availableContactFields,
    ...availableRegularFields,
  ];

  // Entries for regular mapped fields (exclude contact sub-fields from this list)
  const contactFieldKeys = new Set<string>();
  contactFields.forEach((cf) => {
    if (cf.contactFields) {
      Object.keys(cf.contactFields).forEach((subKey) => {
        contactFieldKeys.add(`${cf.name}.${subKey}`);
      });
    }
  });

  const regularEntries = Object.entries(flexAttributes).filter(
    ([key]) => !contactFieldKeys.has(key)
  );

  if (!categoryID) {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Flex Attribute Mapping
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a category first to see available flex attributes.
        </Typography>
      </Box>
    );
  }

  if (flexFieldsConfig.length === 0) {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Flex Attribute Mapping
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No flex attributes configured for this category.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Flex Attribute Mapping
      </Typography>

      {/* Contact type fields - expanded into sub-fields (only shown if user has added them) */}
      {contactFields.map((contactField) => {
        const visibleSubFields = Object.entries(
          contactField.contactFields || {}
        ).filter(([, config]) => config.show);

        if (visibleSubFields.length === 0) return null;

        // Only show if at least one sub-field key exists in flexAttributes
        const hasAnyMapping = visibleSubFields.some(
          ([subKey]) => `${contactField.name}.${subKey}` in flexAttributes
        );
        if (!hasAnyMapping) return null;

        return (
          <Paper
            key={contactField.name}
            variant="outlined"
            sx={{ p: 2, mb: 2 }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5,
              }}
            >
              <Typography variant="body2" fontWeight="bold">
                {contactField.label || contactField.name}
              </Typography>
              <IconButton
                onClick={() => {
                  // Remove all sub-field mappings for this contact field
                  const updated = { ...flexAttributes };
                  Object.keys(updated).forEach((key) => {
                    if (key.startsWith(`${contactField.name}.`)) {
                      delete updated[key];
                    }
                  });
                  updateFlexAttributes(updated);
                }}
                aria-label={`Remove ${contactField.label || contactField.name}`}
                color="error"
                size="small"
              >
                ✕
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {visibleSubFields.map(([subFieldName, subFieldConfig]) => {
                const mappingKey = `${contactField.name}.${subFieldName}`;
                const isRequired = subFieldConfig.required ?? false;
                const label = formatContactFieldLabel(subFieldName);

                return (
                  <Box
                    key={mappingKey}
                    sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 120 }}>
                      {label}
                      {isRequired && (
                        <Typography
                          component="span"
                          color="error"
                          sx={{ ml: 0.5 }}
                        >
                          *
                        </Typography>
                      )}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <ColumnSelect
                        label={`${label} column`}
                        value={flexAttributes[mappingKey] || ''}
                        headers={headers}
                        onChange={(v) => handleColumnChange(mappingKey, v)}
                        required={isRequired}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        );
      })}

      {/* Regular flex fields */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {regularEntries.map(([key, column], index) => (
          <Box
            key={key}
            sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
            data-testid={`flex-attribute-row-${index}`}
          >
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={getFieldLabel(key)}
            >
              {getFieldLabel(key)}
            </Typography>

            <Box sx={{ flex: 1 }}>
              <ColumnSelect
                label="Column"
                value={column}
                headers={headers}
                onChange={(v) => handleColumnChange(key, v)}
              />
            </Box>

            <IconButton
              onClick={() => handleRemoveRow(key)}
              aria-label={`Remove ${getFieldLabel(key)}`}
              color="error"
              size="small"
            >
              ✕
            </IconButton>
          </Box>
        ))}

        {/* Add attribute picker */}
        {availableFields.length > 0 && (
          <Autocomplete
            options={availableFields}
            getOptionLabel={(opt) =>
              `${opt.label || opt.name}${
                opt.type === 'Contact' ? ' (Contact)' : ''
              }`
            }
            onChange={(_e, val) => {
              if (val) handleAddRow(val);
            }}
            value={null}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add flex attribute..."
                size="small"
                placeholder="Search attributes"
              />
            )}
            ListboxProps={{ style: { maxHeight: 200 } }}
            sx={{ maxWidth: 400 }}
            size="small"
            clearOnBlur
            blurOnSelect
          />
        )}

        {availableFields.length === 0 && regularEntries.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            All available flex attributes have been mapped.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/**
 * Formats a camelCase contact field name into a readable label.
 * e.g. "firstName" → "First Name", "phoneNumber" → "Phone Number"
 */
function formatContactFieldLabel(name: string): string {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

export default FlexAttributeMapping;
